import { useCallback, useMemo, useRef } from 'react'

// How far the pointer must travel before the gesture counts as a drag rather than a
// click on a chip. Below this the row does not move and the chip still selects.
const DRAG_THRESHOLD = 4

/**
 * Lets a horizontally overflowing row be panned by dragging it with the mouse.
 * Returns a ref for the scrolling element plus the handlers to spread onto it.
 */
export default function useDragScroll() {
  const ref = useRef(null)
  const dragRef = useRef({
    isPointerDown: false,
    hasDragged: false,
    startX: 0,
    startScrollLeft: 0,
  })

  const handlePointerDown = useCallback((event) => {
    const drag = dragRef.current
    // Cleared for every pointer type: a stale flag left by an earlier mouse drag
    // would otherwise swallow the next tap.
    drag.hasDragged = false
    drag.isPointerDown = false

    // Touch and pen already pan an overflow container natively, and hijacking them
    // would cost the momentum scrolling that comes with it.
    if (event.pointerType !== 'mouse' || event.button !== 0 || !ref.current) {
      return
    }

    drag.isPointerDown = true
    drag.startX = event.clientX
    drag.startScrollLeft = ref.current.scrollLeft
  }, [])

  const handlePointerMove = useCallback((event) => {
    const drag = dragRef.current
    const element = ref.current
    if (!drag.isPointerDown || !element) {
      return
    }

    const delta = event.clientX - drag.startX

    if (!drag.hasDragged) {
      if (Math.abs(delta) <= DRAG_THRESHOLD) {
        return
      }
      drag.hasDragged = true
      // Keeps the move events coming once the pointer leaves the row mid-drag.
      element.setPointerCapture?.(event.pointerId)
    }

    element.scrollLeft = drag.startScrollLeft - delta
  }, [])

  const handlePointerEnd = useCallback((event) => {
    const drag = dragRef.current
    const element = ref.current

    if (element?.hasPointerCapture?.(event.pointerId)) {
      element.releasePointerCapture(event.pointerId)
    }
    drag.isPointerDown = false
  }, [])

  // A drag that ends over a chip still fires a click; swallow it here so panning the
  // row never changes the active tag. Capture phase, so it runs before the chip.
  const handleClickCapture = useCallback((event) => {
    const drag = dragRef.current
    if (!drag.hasDragged) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    drag.hasDragged = false
  }, [])

  const handlers = useMemo(() => ({
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerEnd,
    onPointerCancel: handlePointerEnd,
    onClickCapture: handleClickCapture,
  }), [handlePointerDown, handlePointerMove, handlePointerEnd, handleClickCapture])

  return { ref, handlers }
}
